import {
  MerchantChainConfigStatus,
  WalletSmartAccountType,
} from "@wallet-manager/node-types/dist/src/postgres/const";
import { filter, isNil } from "lodash";
import { createContext, ReactNode, useContext, useMemo } from "react";
import { useQuery } from "react-query";
import { getWalletVersions } from "src/api/merchant";

interface WalletType {
  id: string;
  merchant_id: string;
  chain_type: number;
  chain_id: string;
  wallet_type: number;
  wallet_version: number;
  status: number;
  created_date: string;
  last_modified_date: string;
  wallet_sub_type: number;
  description: string;
}

export type WalletVersionContextValue = {
  list: WalletType[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  filterList: (params: FilterCriteria) => WalletType[];
  getOne: (
    params: FilterCriteria,
    list?: WalletType[] | undefined,
  ) => WalletType | undefined;
};

const WalletVersionContext = createContext<WalletVersionContextValue | null>(
  null,
);

interface FilterCriteria {
  chainType?: number;
  chainId?: number;
  walletVersion?: string;
  smartAccountType?: WalletSmartAccountType;
  walletEnableStatus?: MerchantChainConfigStatus;
  walletTypes?: (string | number)[];
}

const filterList = (list: WalletType[] | undefined, params: FilterCriteria) => {
  const {
    chainType,
    chainId,
    walletVersion,
    smartAccountType,
    walletEnableStatus,
    walletTypes,
  } = params;

  if (!list) {
    return [];
  }

  return list.filter((item) => {
    if (!isNil(chainType) && String(item.chain_type) !== String(chainType)) {
      return false;
    }

    if (!isNil(chainId) && String(item.chain_id) !== String(chainId)) {
      return false;
    }

    if (
      !isNil(walletVersion) &&
      String(item.wallet_version) !== String(walletVersion)
    ) {
      return false;
    }

    if (
      !isNil(smartAccountType) &&
      String(item.wallet_sub_type) !== String(smartAccountType)
    ) {
      return false;
    }

    if (
      !isNil(walletEnableStatus) &&
      String(item.status) !== String(walletEnableStatus)
    ) {
      return false;
    }

    if (!isNil(walletTypes) && !walletTypes.includes(item.wallet_type)) {
      return false;
    }

    return true;
  });
};

export const getOne = (
  list: WalletType[] | undefined,
  params: FilterCriteria,
) => {
  const filtered = filterList(list, params);
  return filtered.length > 0 ? filtered[0] : undefined;
};

export function WalletVersionProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: ["walletVersions"],
    queryFn: async () => {
      return getWalletVersions();
    },
    enabled: true,
  });

  const value = useMemo(
    () => ({
      isLoading: query.isLoading,
      isError: query.isError,
      refetch: query.refetch,
      list: query.data as WalletType[] | undefined,
      filterList: (params: FilterCriteria) =>
        filterList(query.data as WalletType[] | undefined, params),
      getOne: (params: FilterCriteria, list?: WalletType[] | undefined) =>
        getOne(list ? list : (query.data as WalletType[]), params),
    }),
    [query.isLoading, query.isError, query.refetch, query.data],
  );

  return (
    <WalletVersionContext.Provider value={value}>
      {children}
    </WalletVersionContext.Provider>
  );
}

export function useWalletVersionContext() {
  const ctx = useContext(WalletVersionContext);
  if (!ctx) {
    throw new Error(
      "useWalletVersionContext must be used within WalletVersionProvider",
    );
  }
  return ctx;
}
